import { fitText } from "@/lib/fit-title";
import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { absoluteUrl } from "@/config/sites";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { MLT_DOC_TYPES, MLT_LANGUAGES } from "@/data/mlt/keywords-500";
import { ServicePlaybook } from "@/components/ServicePlaybook";
import { buildClusterPlaybook } from "@/data/_playbook/clusters";

type Doc = (typeof MLT_DOC_TYPES)[number];

/** Deterministic per-document detail so every page carries unique editorial content. */
const ISSUERS: Record<string, string> = {
  "birth-certificate": "สำนักงานเขต/ที่ว่าการอำเภอที่แจ้งเกิด",
  "marriage-certificate": "สำนักงานเขต/ที่ว่าการอำเภอที่จดทะเบียนสมรส",
  "divorce-certificate": "สำนักงานเขต/ที่ว่าการอำเภอที่จดทะเบียนหย่า",
  "death-certificate": "สำนักงานเขต/ที่ว่าการอำเภอที่แจ้งตาย",
  "name-change": "สำนักงานเขต/ที่ว่าการอำเภอ (ฝ่ายทะเบียนทั่วไป)",
  "house-registration": "สำนักงานเขต/ที่ว่าการอำเภอ",
  "police-clearance": "กองบัญชาการตำรวจสันติบาล",
  degree: "สถาบันการศึกษาผู้ออกปริญญาบัตร",
  transcript: "สำนักทะเบียนของสถาบันการศึกษา",
  passport: "กรมการกงสุล กระทรวงการต่างประเทศ",
  "company-affidavit": "กรมพัฒนาธุรกิจการค้า (DBD)",
  "financial-statement": "ผู้สอบบัญชีรับอนุญาต / DBD",
  "shareholder-list": "กรมพัฒนาธุรกิจการค้า (บอจ.5)",
  "articles-of-association": "กรมพัฒนาธุรกิจการค้า",
  "business-license": "หน่วยงานผู้ออกใบอนุญาตตามประเภทธุรกิจ",
  "commercial-contract": "คู่สัญญา (เอกสารเอกชน)",
  "power-of-attorney": "ผู้มอบอำนาจ (เอกสารเอกชน)",
  "court-judgment": "ศาลที่มีคำพิพากษา",
  "medical-certificate": "โรงพยาบาลหรือคลินิกผู้ออกเอกสาร",
  "vaccination-record": "สถานพยาบาลผู้ให้วัคซีน",
  "driving-license": "กรมการขนส่งทางบก",
  "bank-statement": "ธนาคารเจ้าของบัญชี",
  "employment-contract": "นายจ้าง (เอกสารเอกชน)",
  "insurance-policy": "บริษัทประกันภัย",
  will: "ผู้ทำพินัยกรรม / สำนักงานเขตกรณีพินัยกรรมฝ่ายเมือง",
};

const USES: Record<string, string> = {
  "birth-certificate": "ขอสัญชาติ ขอวีซ่าติดตามครอบครัว จดทะเบียนบุตรที่สถานทูต และสมัครเรียนต่างประเทศ",
  "marriage-certificate": "ขอวีซ่าคู่สมรส บันทึกทะเบียนสมรสในต่างประเทศ เปลี่ยนนามสกุลในเอกสารต่างประเทศ",
  "divorce-certificate": "จดทะเบียนสมรสใหม่ในต่างประเทศ ยืนยันสถานภาพสมรส และคดีครอบครัว",
  "death-certificate": "จัดการมรดกข้ามประเทศ เคลมประกันชีวิต และปิดบัญชีธนาคารต่างประเทศ",
  "name-change": "ยืนยันความสอดคล้องของชื่อในหนังสือเดินทางกับเอกสารอื่น",
  "house-registration": "ยืนยันที่อยู่สำหรับวีซ่า การขอสินเชื่อ และการจดทะเบียนธุรกิจในต่างประเทศ",
  "police-clearance": "ขอวีซ่าถาวร ใบอนุญาตทำงาน และการรับรองประวัติเพื่อสมัครงานต่างประเทศ",
  degree: "สมัครเรียนต่อ ขอใบอนุญาตประกอบวิชาชีพ และสมัครงานในต่างประเทศ",
  transcript: "เทียบโอนหน่วยกิต สมัครปริญญาโท-เอก และประเมินวุฒิการศึกษา",
  passport: "ยืนยันตัวตนประกอบชุดเอกสารที่ยื่นต่อหน่วยงานต่างประเทศ",
  "company-affidavit": "เปิดบริษัทลูก เปิดบัญชีธนาคารต่างประเทศ เข้าร่วมประมูล และทำสัญญาข้ามประเทศ",
  "financial-statement": "ยื่นขอสินเชื่อ ขอวีซ่านักลงทุน และรายงานต่อบริษัทแม่ต่างประเทศ",
  "shareholder-list": "ตรวจสอบโครงสร้างผู้ถือหุ้นในการทำ due diligence และการลงทุน",
  "articles-of-association": "จดทะเบียนสาขาต่างประเทศ และการทำธุรกรรมองค์กร",
  "business-license": "ยืนยันสิทธิประกอบธุรกิจต่อคู่ค้าและหน่วยงานต่างประเทศ",
  "commercial-contract": "ใช้อ้างอิงในการค้าระหว่างประเทศและกระบวนการอนุญาโตตุลาการ",
  "power-of-attorney": "มอบอำนาจดำเนินการแทนในต่างประเทศ เช่น ขายทรัพย์ จัดการบัญชี จัดการมรดก",
  "court-judgment": "บังคับคดีข้ามประเทศ คดีหย่า และคดีอำนาจปกครองบุตร",
  "medical-certificate": "ขอวีซ่า สมัครงาน และเคลมประกันสุขภาพต่างประเทศ",
  "vaccination-record": "การเดินทาง เข้าเรียน และการทำงานในต่างประเทศ",
  "driving-license": "ขอใบขับขี่ต่างประเทศและใบขับขี่สากล",
  "bank-statement": "แสดงหลักฐานการเงินสำหรับวีซ่าและการยื่นสินเชื่อ",
  "employment-contract": "ขอวีซ่าทำงาน ยืนยันรายได้ และประกอบการยื่นภาษี",
  "insurance-policy": "เคลมสินไหมข้ามประเทศ และเงื่อนไขวีซ่าที่ต้องมีประกัน",
  will: "จัดการมรดกและทรัพย์สินที่อยู่ต่างประเทศ",
};

const CAUTIONS: Record<string, string> = {
  "police-clearance": "ใบตรวจประวัติมีอายุการยอมรับสั้น หลายประเทศรับเฉพาะที่ออกไม่เกิน 3-6 เดือน ควรวางแผนวันแปลและรับรองให้ทันกำหนดยื่น",
  "financial-statement": "ต้องเป็นงบที่นำส่ง DBD แล้วและมีลายมือชื่อผู้สอบบัญชี มิฉะนั้นกรมการกงสุลจะไม่รับรอง",
  "court-judgment": "ต้องขอสำเนาคำพิพากษาที่ศาลรับรองสำเนาถูกต้องพร้อมหนังสือรับรองคดีถึงที่สุดก่อน",
  will: "พินัยกรรมที่ทำในไทยควรตรวจความสมบูรณ์ตามกฎหมายไทยก่อนแปล เพื่อลดความเสี่ยงถูกปฏิเสธในต่างประเทศ",
};

const DEFAULT_CAUTION =
  "กรมการกงสุลรับรองเฉพาะเอกสารที่ออกโดยหน่วยงานราชการหรือมีการรับรองสำเนาถูกต้องอย่างถูกวิธี เอกสารเอกชนต้องผ่านการรับรองลายมือชื่อโดย Notary Public ก่อน";

function priceFor(slug: string) {
  const idx = MLT_DOC_TYPES.findIndex((d) => d.slug === slug);
  const base = 500 + (idx % 5) * 100;
  return { base, third: base + 500 };
}

export const Route = createFileRoute("/translation/documents/$doc")({
  loader: (__ctx) => { const __d = (({ params }) => {
    const doc: Doc | undefined = MLT_DOC_TYPES.find((x) => x.slug === params.doc);
    if (!doc) throw notFound();
    return { doc };
  })(__ctx); return { ...__d, h: (({ loaderData }) => {
    if (!loaderData) return { meta: [{ title: ctrTitle("ไม่พบหน้า") }, { name: "robots", content: "noindex" }] };
    const d = loaderData.doc;
    const url = absoluteUrl(`/translation/documents/${d.slug}`);
    const p = priceFor(d.slug);
    const title = fitText(`แปล${d.th} รับรองกงสุล-สถานทูต | Thai Notary Law`, 60);
    const desc =
      fitText(`แปล${d.th} (${d.en}) ทุกภาษา พร้อมรับรองคำแปล กรมการกงสุล และสถานทูต สอบถามเจ้าหน้าที่เพื่อขอใบเสนอราคา`, 160);
    return {
      meta: [
        { title },
        { name: "description", content: ctrDesc(desc) },
        { property: "og:title", content: ctrTitle(title) },
        { property: "og:description", content: ctrDesc(desc) },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: SOCIAL_OG_IMAGE },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Service",
                name: `แปล${d.th}พร้อมรับรอง`,
                serviceType: `${d.en} certified translation`,
                areaServed: "TH",
                url,
                provider: { "@type": "LegalService", name: "Thai Notary Law & Service Co., Ltd.", telephone: "+66948958999" },
              },
              {
                "@type": "FAQPage",
                mainEntity: [
                  {
                    "@type": "Question",
                    name: `แปล${d.th}ใช้เวลากี่วัน`,
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: `งานแปล ${d.th} ปกติเสร็จภายใน 1-2 วันทำการ หากต้องรับรองกรมการกงสุลเพิ่มอีก 2-3 วันทำการ และหากต้องรับรองสถานทูตปลายทางเพิ่มอีก 1-5 วันทำการตามระเบียบของแต่ละสถานทูต`,
                    },
                  },
                  {
                    "@type": "Question",
                    name: `แปล${d.th}ราคาเท่าไหร่`,
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: `ค่าบริการแปลขึ้นกับภาษาและจำนวนหน้า สอบถามเจ้าหน้าที่เพื่อขอใบเสนอราคา ส่วนค่าธรรมเนียมนิติกรณ์กรมการกงสุล 200 บาท/แผ่นประทับ (แบบด่วน 400 บาท) ข้อมูล ณ ก.ค. 2569 ค่ารับรองสถานทูตขึ้นกับอัตราของแต่ละประเทศ`,
                    },
                  },
                  {
                    "@type": "Question",
                    name: `แปล${d.th}ต้องใช้เอกสารอะไรบ้าง`,
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: `ต้นฉบับหรือสำเนารับรองถูกต้องจาก${ISSUERS[d.slug] ?? "หน่วยงานผู้ออกเอกสาร"} สำเนาหนังสือเดินทางหรือบัตรประชาชนของเจ้าของเอกสาร และหนังสือมอบอำนาจกรณีให้ดำเนินการแทน`,
                    },
                  },
                ],
              },
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "หน้าแรก", item: absoluteUrl("/") },
                  { "@type": "ListItem", position: 2, name: "แปลและรับรองเอกสาร", item: absoluteUrl("/translation") },
                  { "@type": "ListItem", position: 3, name: `แปล${d.th}`, item: url },
                ],
              },
            ],
          }),
        },
      ],
    };
  })({ ...__ctx, loaderData: __d }) }; },
  head: ({ loaderData }) => loaderData?.h ?? {},
  component: DocPage,
  notFoundComponent: () => <div className="p-8 text-center">ไม่พบประเภทเอกสารนี้</div>,
});

function DocPage() {
  const { doc } = Route.useLoaderData();
  const p = priceFor(doc.slug);
  const issuer = ISSUERS[doc.slug] ?? "หน่วยงานผู้ออกเอกสาร";
  const use = USES[doc.slug] ?? "การยื่นต่อหน่วยงานราชการและเอกชนในต่างประเทศ";
  const caution = CAUTIONS[doc.slug] ?? DEFAULT_CAUTION;
  const others = MLT_DOC_TYPES.filter((d) => d.slug !== doc.slug).slice(0, 9);

  return (
    <>
      <Breadcrumbs
        items={[
          { label: "หน้าแรก", to: "/" },
          { label: "แปลและรับรองเอกสาร", to: "/translation" },
          { label: `แปล${doc.th}` },
        ]}
      />
      <section className="mx-auto max-w-4xl px-4 py-10">
        <h1 className="text-3xl font-bold mb-4">แปล{doc.th} พร้อมรับรองคำแปลทุกภาษา</h1>
        <p className="answer-summary text-lg p-4 bg-muted rounded-xl mb-8" data-speakable>
          แปล{doc.th} ({doc.en}) จากไทยเป็นภาษาต่างประเทศหรือจากภาษาต่างประเทศเป็นไทย พร้อมรับรองคำแปล
          รับรองกรมการกงสุล และสถานทูตปลายทาง ค่าบริการขึ้นกับภาษา จำนวนหน้า และหน่วยงานปลายทาง
          สอบถามเจ้าหน้าที่เพื่อขอใบเสนอราคา
        </p>

        <div className="grid gap-4 md:grid-cols-2 mb-10">
          <div className="p-4 border rounded-xl">
            <h2 className="font-semibold mb-1">หน่วยงานผู้ออกเอกสาร</h2>
            <p className="text-sm text-muted-foreground">{issuer}</p>
          </div>
          <div className="p-4 border rounded-xl">
            <h2 className="font-semibold mb-1">ใช้ทำอะไรได้บ้าง</h2>
            <p className="text-sm text-muted-foreground">{use}</p>
          </div>
        </div>

        <h2 className="text-xl font-semibold mb-3">เอกสารที่ต้องเตรียม</h2>
        <ul className="list-disc pl-6 space-y-1 text-sm mb-10">
          <li>ต้นฉบับ{doc.th} หรือสำเนาที่รับรองถูกต้องจาก{issuer}</li>
          <li>สำเนาหนังสือเดินทางหรือบัตรประชาชนของเจ้าของเอกสาร</li>
          <li>สะกดชื่อ-นามสกุลภาษาอังกฤษตามหนังสือเดินทาง เพื่อให้คำแปลตรงกันทุกฉบับ</li>
          <li>หนังสือมอบอำนาจ กรณีให้ทีมงานยื่นกรมการกงสุลหรือสถานทูตแทน</li>
        </ul>

        <h2 className="text-xl font-semibold mb-3">ข้อควรระวัง</h2>
        <p className="faq-answer text-sm text-muted-foreground mb-10">{caution}</p>

        <h2 className="text-xl font-semibold mb-3">แปล{doc.th}เป็นภาษาอื่น</h2>
        <ul className="grid gap-2 md:grid-cols-3 text-sm mb-10">
          {MLT_LANGUAGES.slice(0, 12).map((l) => (
            <li key={l.slug}>
              <Link to="/translation/lang/$lang" params={{ lang: l.slug }} className="text-blue-700 hover:underline">
                แปล{doc.th}เป็นภาษา{l.th}
              </Link>
            </li>
          ))}
        </ul>

        <h2 className="text-xl font-semibold mb-3">เอกสารประเภทอื่นที่ลูกค้ามักแปลพร้อมกัน</h2>
        <ul className="grid gap-2 md:grid-cols-3 text-sm mb-10">
          {others.map((d) => (
            <li key={d.slug}>
              <Link to="/translation/documents/$doc" params={{ doc: d.slug }} className="text-blue-700 hover:underline">
                แปล{d.th}
              </Link>
            </li>
          ))}
        </ul>

        <div className="p-6 bg-muted rounded-xl">
          <p className="font-semibold">
            ส่งไฟล์ประเมินราคาฟรี โทร{" "}
            <a href="tel:0948958999" className="text-blue-700">
              094-895-8999
            </a>{" "}
            · LINE @Thainotary
          </p>
        </div>
      </section>
      <ServicePlaybook
        url={absoluteUrl(`/translation/documents/${doc.slug}`)}
        subject={doc.th}
        playbook={buildClusterPlaybook("mlt", { nameTh: doc.th, nameEn: doc.en })}
      />
    </>
  );
}
